<script setup>
import { computed, onMounted, ref } from "vue";
import HeaderTitle from "@/components/layout/HeaderTitle.vue";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import Notification from "@/utils/plugins/NotificationService";
import { normalizedIncludes } from "@/utils/textSearch";
import { useUiStore } from "@/app/stores/uiStore";
import SocialFundCallPeriodService from "@/api/services/socialFundCallPeriod.service";
import SocialFundCallPeriodDialog from "@/components/shared/SocialFund/SocialFundCallPeriodDialog.vue";
import SocialFundAgreementDialog from "@/components/shared/SocialFund/SocialFundAgreementDialog.vue";

const uiStore = useUiStore();

const page = ref(1);
const itemsPerPage = ref(10);
const searchTerm = ref("");
const selectedStatus = ref("");

const calls = ref([]);
const rawCallsById = ref({});
const showCallDialog = ref(false);
const editingCall = ref(null);
const isSavingCall = ref(false);
const showAgreementDialog = ref(false);
const agreementCall = ref(null);

// El backend (SocialFundCallPeriod) solo modela "active" + fechas -> abierta/
// cerrada (isOpen calculado). No existe un tercer estado "archivada" todavía;
// se deja la opción en el filtro por si se añade más adelante, pero hoy nunca
// habrá convocatorias que la cumplan.
const statusOptions = [
  { value: "open", label: "Abierta" },
  { value: "closed", label: "Cerrada" },
  { value: "archived", label: "Archivada" },
];

const statusLabel = (status) =>
  statusOptions.find((option) => option.value === status)?.label || "Sin estado";

const formatDate = (value) => {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(value));
};

const mapCall = (item) => ({
  id: item.id,
  year: item.year,
  label: item.label || item.year,
  openingDate: formatDate(item.startDate),
  closingDate: formatDate(item.endDate),
  status: item.isOpen ? "open" : "closed",
  requests: item.applicationsCount,
});

const getCalls = async () => {
  uiStore.startLoading();
  try {
    const res = await SocialFundCallPeriodService.getCallPeriods();
    const items = res?.data["hydra:member"] || [];
    calls.value = items.map(mapCall);
    rawCallsById.value = Object.fromEntries(items.map((item) => [item.id, item]));
  } catch {
    Notification.addNotification("Error al cargar las convocatorias de Fondo Social.", "error", 6000);
  } finally {
    uiStore.stopLoading();
  }
};

onMounted(getCalls);

// Filtro y paginación en cliente: la colección de convocatorias es pequeña
// (una por año) y el endpoint no pagina (paginationEnabled: false), a
// diferencia del listado de solicitudes.
const filteredCalls = computed(() =>
  calls.value.filter((call) => {
    const matchesText =
      !searchTerm.value ||
      normalizedIncludes(`${call.year} ${call.label}`, searchTerm.value);
    const matchesStatus = !selectedStatus.value || call.status === selectedStatus.value;

    return matchesText && matchesStatus;
  })
);

const listedCalls = computed(() => {
  const firstItem = (page.value - 1) * itemsPerPage.value;
  return filteredCalls.value.slice(firstItem, firstItem + itemsPerPage.value);
});

const search = () => {
  page.value = 1;
};

const handlePageChange = () => {
  const lastPage = Math.max(1, Math.ceil(filteredCalls.value.length / itemsPerPage.value));
  if (page.value > lastPage) {
    page.value = lastPage;
  }
};

const startNewCall = () => {
  editingCall.value = null;
  showCallDialog.value = true;
};

const editCall = (call) => {
  const raw = rawCallsById.value[call.id];
  editingCall.value = {
    id: call.id,
    year: raw.year,
    label: raw.label,
    startDateRaw: raw.startDate ? raw.startDate.slice(0, 10) : "",
    endDateRaw: raw.endDate ? raw.endDate.slice(0, 10) : "",
    active: raw.active,
  };
  showCallDialog.value = true;
};

const openAgreementDialog = (call) => {
  agreementCall.value = call;
  showAgreementDialog.value = true;
};

const saveCall = async (payload) => {
  const { agreementFile, ...callPeriodPayload } = payload;

  isSavingCall.value = true;
  uiStore.startLoading();
  try {
    if (editingCall.value) {
      await SocialFundCallPeriodService.updateCallPeriod(editingCall.value.id, callPeriodPayload);
      Notification.success("Convocatoria actualizada correctamente.", 3500);
    } else {
      // 2026-09-29: una convocatoria nunca puede quedarse sin convenio, así
      // que crearla y subir el convenio son un único paso para quien la
      // gestiona, aunque sean 2 peticiones HTTP (no hay endpoint combinado).
      const createRes = await SocialFundCallPeriodService.createCallPeriod(callPeriodPayload);
      const newCallPeriodId = createRes?.data?.id;
      try {
        await SocialFundCallPeriodService.uploadAgreement(newCallPeriodId, agreementFile);
        Notification.success("Convocatoria y convenio creados correctamente.", 3500);
      } catch (agreementErr) {
        const message = agreementErr?.response?.data?.error || "No se ha podido subir el convenio.";
        Notification.addNotification(
          `La convocatoria se creó, pero ${message.charAt(0).toLowerCase()}${message.slice(1)} Vuelve a intentarlo desde "VER CONVENIO".`,
          "error",
          8000,
        );
      }
    }
    showCallDialog.value = false;
    await getCalls();
  } catch (err) {
    const message = err?.response?.data?.violations?.[0]?.message
      || err?.response?.data?.detail
      || "No se ha podido guardar la convocatoria. Comprueba que el año no esté repetido y que las fechas sean correctas.";
    Notification.addNotification(message, "error", 6000);
  } finally {
    isSavingCall.value = false;
    uiStore.stopLoading();
  }
};
</script>

<template>
  <main class="social-fund">
    <HeaderTitle title="Fondo social" />

    <section class="social-fund__content">
      <div class="social-fund__section-title">
        <svg class="social-fund__search-icon" viewBox="0 0 1024 1024" aria-hidden="true">
          <path
            d="M641.8 737.4c-66.4 47.3-147.7 75.2-235.5 75.2-224.4 0-406.3-181.9-406.3-406.3s181.9-406.3 406.3-406.3c224.4 0 406.3 181.9 406.3 406.3 0 87.8-27.8 169.1-75.2 235.5l266.9 266.9c26.3 26.3 26.1 68.4 0 94.5l-1.1 1.1c-26 26-68.4 26.1-94.5 0l-266.9-266.9zM406.3 717c171.6 0 310.7-139.1 310.7-310.7s-139.1-310.7-310.7-310.7-310.7 139.1-310.7 310.7S234.7 717 406.3 717z" />
        </svg>
        <h2>Convocatorias</h2>
      </div>

      <el-card class="card-search social-fund__filters w-100" body-style="padding:0">
        <div class="social-fund__filters-content">
          <div class="row social-fund__filter-row">
            <div class="col-md-4 col-12">
              <div class="form-item">
                <label for="social-fund-search">Nombre convocatoria</label>
                <el-input id="social-fund-search" v-model="searchTerm" class="w-100 social-fund__search-input"
                  placeholder="Buscar por año o etiqueta..." clearable @change="search" @clear="search" />
              </div>
            </div>
            <div class="col-md-3 col-12">
              <div class="form-item">
                <label for="social-fund-status">Estado</label>
                <el-select id="social-fund-status" v-model="selectedStatus" class="w-100"
                  placeholder="Todos los estados" clearable filterable @change="search">
                  <el-option v-for="option in statusOptions" :key="option.value" :label="option.label"
                    :value="option.value" />
                </el-select>
              </div>
            </div>
            <div class="col-md-3 col-12 social-fund__new-group">
              <button type="button" class="btn btn_yellow social-fund__new-button" @click="startNewCall">
                <span aria-hidden="true">+</span> NUEVA CONVOCATORIA
              </button>
              <p class="social-fund__notice">
                <span aria-hidden="true">i</span>
                Solo puede existir una convocatoria abierta.
              </p>
            </div>
            <div class="col-md-2 col-12 social-fund__search-action">
              <button type="button" class="btn btn_red btn-search" @click="search">BUSCAR</button>
            </div>
          </div>
        </div>
      </el-card>

      <div class="table table-not-mobile social-fund__table">
        <table>
          <thead>
            <tr>
              <th>Año</th>
              <th>Etiqueta</th>
              <th>Apertura</th>
              <th>Cierre</th>
              <th>Estado</th>
              <th>Solicitudes</th>
              <th>Convenio</th>
              <th class="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="call in listedCalls" :key="call.id">
              <td class="social-fund__year">{{ call.year }}</td>
              <td>{{ call.label }}</td>
              <td>{{ call.openingDate }}</td>
              <td>{{ call.closingDate }}</td>
              <td><span class="social-fund__status" :class="`social-fund__status--${call.status}`">{{
                statusLabel(call.status) }}</span></td>
              <td class="text-center">{{ call.requests }}</td>
              <td>
                <button type="button" class="social-fund__agreement-button" @click="openAgreementDialog(call)">
                  VER CONVENIO
                </button>
              </td>
              <td class="text-center">
                <font-awesome-layers class="fa-2x social-fund__edit-action" title="Editar convocatoria"
                  @click="editCall(call)">
                  <font-awesome-icon icon="circle" style="color: #f7d20e" />
                  <font-awesome-icon icon="pen" class="fa-inverse" transform="shrink-7" />
                </font-awesome-layers>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="!filteredCalls.length" class="social-fund__empty">No se han encontrado convocatorias con los filtros
          seleccionados.</p>
      </div>

      <div class="table-mobile social-fund__mobile-list">
        <el-card v-for="call in listedCalls" :key="call.id" class="social-fund__mobile-card" @click="editCall(call)">
          <div class="social-fund__mobile-card-title">
            <span>{{ call.label }}</span>
            <span class="social-fund__status" :class="`social-fund__status--${call.status}`">{{ statusLabel(call.status)
            }}</span>
          </div>
          <p><b>Año:</b> {{ call.year }}</p>
          <p><b>Apertura:</b> {{ call.openingDate }}</p>
          <p><b>Cierre:</b> {{ call.closingDate }}</p>
          <p><b>Solicitudes:</b> {{ call.requests }}</p>
          <button type="button" class="social-fund__agreement-button" @click.stop="openAgreementDialog(call)">
            VER CONVENIO
          </button>
        </el-card>
        <p v-if="!filteredCalls.length" class="social-fund__empty">No se han encontrado convocatorias con los filtros
          seleccionados.</p>
      </div>

      <SharedElementPagination v-model:current-page="page" v-model:page-size="itemsPerPage"
        :total="filteredCalls.length" :page-size-options="[10, 20, 50]" class="social-fund__pagination"
        @change="handlePageChange" @size-change="page = 1" />
    </section>

    <SocialFundCallPeriodDialog v-model="showCallDialog" :call="editingCall" :saving="isSavingCall" @submit="saveCall" />
    <SocialFundAgreementDialog v-model="showAgreementDialog" :call-period-id="agreementCall?.id"
      :call-period-label="agreementCall?.label" />
  </main>
</template>

<style lang="scss" scoped>
.social-fund {
  color: #263052;
}

.social-fund__content {
  padding-bottom: 26px;
}

.social-fund__section-title {
  align-items: center;
  color: $bg-blue;
  display: flex;
  gap: 12px;
  margin: 0 0 14px;
}

.social-fund__search-icon {
  fill: $bg-blue;
  height: 42px;
  width: 42px;
}

.social-fund__section-title h2 {
  font-size: 2.5em;
  font-weight: 900;
  letter-spacing: -0.05em;
  margin: 0;
}

.social-fund__filters {
  background: #fff !important;
  border-radius: 15px;
  overflow: hidden;
}

.social-fund__filters :deep(.el-card__body) {
  padding: 0 !important;
}

.social-fund__new-button,
.social-fund__search-action .btn {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  margin: 0 !important;
  min-height: 44px;
}

.social-fund__new-button {
  color: #474ce6 !important;
  font-weight: 800;
  gap: 8px;
  padding: 0 20px;
  width: 100%;
}

.social-fund__new-button span {
  font-size: 23px;
  font-weight: 400;
  line-height: 1;
}

.social-fund__notice {
  align-items: center;
  background: #f1f1ff;
  border-radius: 8px;
  color: #4f5674 !important;
  display: flex;
  font-size: 14px;
  gap: 8px;
  margin: 12px 0 0;
  padding: 11px 13px;
  width: 100%;
}

.social-fund__notice span {
  align-items: center;
  border: 1px solid $bg-blue;
  border-radius: 50%;
  color: $bg-blue;
  display: inline-flex;
  font-size: 11px;
  font-weight: 800;
  height: 18px;
  justify-content: center;
  width: 18px;
}

.social-fund__filter-row {
  align-items: flex-start;
  row-gap: 16px;
}

.social-fund__filter-row .form-item label {
  color: #283150;
  font-size: 16px;
  font-weight: 800;
  margin-bottom: 7px;
}

.social-fund__filter-row .form-item {
  margin-bottom: 0;
}

.social-fund__search-input :deep(.el-input__prefix) {
  display: none;
}

.social-fund__search-action {
  align-items: flex-start;
  display: flex;
  justify-content: flex-end;
  padding-top: 30px;
}

.social-fund__search-action .btn {
  color: #fff !important;
  font-weight: 800;
  width: 100%;
}

.social-fund__new-group {
  padding-top: 30px;
}

.social-fund__table {
  margin-top: 20px;
}

.social-fund__table table {
  min-width: 900px;
}

.social-fund__table th {
  color: $bg-blue;
  font-size: 15px;
  text-transform: uppercase;
}

.social-fund__table td {
  vertical-align: middle;
}

.social-fund__year {
  color: #1e2646;
  font-weight: 900;
}

.social-fund__status {
  align-items: center;
  border: 1px solid #c5c8d5;
  border-radius: 16px;
  display: inline-flex;
  font-size: 14px;
  font-weight: 700;
  gap: 6px;
  line-height: 1;
  padding: 6px 10px;
  white-space: nowrap;
}

.social-fund__status::before {
  background: #979daa;
  border-radius: 50%;
  content: "";
  display: inline-block;
  height: 8px;
  width: 8px;
}

.social-fund__status--open {
  background: #ebfff0;
  border-color: #65bf7a;
  color: #238a43;
}

.social-fund__status--open::before {
  background: #16963e;
}

.social-fund__status--closed,
.social-fund__status--archived {
  background: #f8f8fb;
  color: #6c7283;
}

.social-fund__edit-action {
  cursor: pointer;
}

.social-fund__agreement-button {
  background: #fff;
  border: 1px solid $bg-blue;
  border-radius: 20px;
  color: $bg-blue;
  font-size: 11px;
  font-weight: 800;
  padding: 6px 12px;
  white-space: nowrap;
}

.social-fund__empty {
  color: #747d9f;
  margin: 22px 0;
  text-align: center;
}

.table-mobile {
  display: none;
}

.social-fund__pagination {
  display: flex;
  justify-content: center;
  margin: 22px 0 0;
}

@media (max-width: 767px) {
  .social-fund__section-title h2 {
    font-size: 2em;
  }

  .social-fund__search-icon {
    height: 34px;
    width: 34px;
  }

  .social-fund__filters-content {
    padding: 18px 16px;
  }

  .social-fund__notice {
    line-height: 1.35;
  }

  .social-fund__new-group,
  .social-fund__search-action {
    padding-top: 0;
  }

  .social-fund__search-action .btn {
    width: 100%;
  }

  .table-not-mobile {
    display: none;
  }

  .table-mobile {
    display: grid;
    gap: 12px;
    margin-top: 18px;
  }

  .social-fund__mobile-card {
    border-radius: 16px;
    cursor: pointer;
  }

  .social-fund__mobile-card :deep(.el-card__body) {
    padding: 16px;
  }

  .social-fund__mobile-card-title {
    align-items: flex-start;
    color: $bg-blue;
    display: flex;
    font-size: 17px;
    font-weight: 900;
    gap: 12px;
    justify-content: space-between;
    line-height: 1.25;
    margin-bottom: 13px;
  }

  .social-fund__mobile-card p {
    display: grid;
    gap: 8px;
    grid-template-columns: 108px minmax(0, 1fr);
    margin: 8px 0 0;
    word-break: break-word;
  }

  .social-fund__mobile-card p b {
    color: $bg-blue;
  }

  .social-fund__mobile-card .social-fund__agreement-button {
    margin-top: 12px;
    width: 100%;
  }

  .social-fund__pagination {
    max-width: 100%;
    overflow: hidden;
  }
}
</style>
